/**
 * Wave BT — P&ID Document-First Workflow.
 *
 * Mirrors the PFD document-register UX for P&IDs: the user sees the
 * full N-sheet register (33 for a refinery, 551 for LNG, 1,156 for a
 * full refinery + utilities, etc.) and clicks "Generate" on each row
 * to render an ISO-standard P&ID with ISA-5.1 instrumentation.
 *
 * Endpoints:
 *   GET    /api/projects/:id/pid/index               → fetch register
 *   POST   /api/projects/:id/pid/create-index        → rebuild register
 *   GET    /api/projects/:id/pid-generation-state    → per-sheet status
 *   GET    /api/projects/:id/pid/drawings            → list rendered SVGs
 *   POST   /api/projects/:id/pid/generate/:n         → render one P&ID
 *   POST   /api/projects/:id/pid/generate-all        → render all
 *   GET    /api/projects/:id/drawings/:sheet_id      → fetch one SVG
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Loader2, FileText, Eye, Play, RefreshCcw,
   AlertCircle, CheckCircle2, ZoomIn, ZoomOut, Maximize2, Minimize2,
   GitCompareArrows, Pencil, MousePointerClick }
   from "lucide-react";
import PidDiffPanel from "./PidDiffPanel";
import PidEditor from "./PidEditor";
import ApsDrawingViewer from "./ApsDrawingViewer";
import InteractivePidPanel from "./InteractivePidPanel";

// Client-side content hash that mirrors the backend's
// hashlib.sha256(svg).hexdigest()[:16] so we can detect when an
// editor save has produced a newer SVG than the last APS translation.
function _hashSvgFnv(s) {
  if (!s) return "";
  let h1 = 0x811c9dc5, h2 = 0xdeadbeef;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 ^ c, 2166136261) >>> 0;
  }
  return (h1.toString(16).padStart(8, "0")
        + h2.toString(16).padStart(8, "0")).slice(0, 16);
}

const COL = {
   bg: "var(--pf-bg, #020617)",
   surface: "var(--pf-surface, #0f1729)",
   surface2: "var(--pf-surface-2, #1a2332)",
   border: "var(--pf-border, #1e293b)",
   text: "var(--pf-text, #e2e8f0)",
   dim: "var(--pf-text-muted, #94a3b8)",
   spark: "var(--pf-spark, #22d3ee)",
   amber: "#f59e0b",
   green: "#22c55e",
   red: "#ef4444",
};
const mono = { fontFamily: "'JetBrains Mono', 'Fira Code', monospace" };
const STATUS_ICONS = {
   not_started: "⬜",
   generating: "🔄",
   complete: "✅",
   failed: "❌",
};

export default function PidDocumentRegister({ projectId }) {
   const [activeTab, setActiveTab] = useState("documents");
   const [index, setIndex] = useState(null);
   const [state, setState] = useState(null);
   const [drawings, setDrawings] = useState({});
   const [activeSheetId, setActiveSheetId] = useState(null);
   const [openSheetIds, setOpenSheetIds] = useState([]);
   const [creating, setCreating] = useState(false);
   const [generatingAll, setGeneratingAll] = useState(false);
   const [err, setErr] = useState("");
   const [viewerZoom, setViewerZoom] = useState(1);
   const [viewerPan, setViewerPan] = useState({ x: 0, y: 0 });
   const [viewerFullscreen, setViewerFullscreen] = useState(false);
   const [unitFilter, setUnitFilter] = useState("all"); // unit_key | "all"
   const [interactiveSheet, setInteractiveSheet] = useState(null);
   const isPanningRef = useRef(false);
   const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

   // ── Loaders ──
   const loadIndex = useCallback(async () => {
      try {
         const r = await api.get(`/projects/${projectId}/pid/index`);
         if (r.data && Array.isArray(r.data.sheets) && r.data.sheets.length) {
            setIndex(r.data);
         } else {
            setIndex(null);
         }
      } catch (_e) { /* 404 → no index yet */ }
   }, [projectId]);

   const loadState = useCallback(async () => {
      try {
         const r = await api.get(`/projects/${projectId}/pid-generation-state`);
         setState(r.data);
      } catch (_e) { /* no state yet */ }
   }, [projectId]);

   const loadDrawings = useCallback(async () => {
      try {
         const r = await api.get(`/projects/${projectId}/pid/drawings`);
         const list = r.data?.drawings || [];
         setDrawings((prev) => {
            const next = {};
            for (const d of list) {
               const had = prev[d.sheet_id];
               next[d.sheet_id] = (had && had.canvas_svg && !d.canvas_svg)
                  ? { ...d, canvas_svg: had.canvas_svg }
                  : d;
            }
            return next;
         });
      } catch (_e) { /* */ }
   }, [projectId]);

   useEffect(() => {
      loadIndex();
      loadState();
      loadDrawings();
   }, [loadIndex, loadState, loadDrawings]);

   // ── Actions ──
   const createIndex = async () => {
      setCreating(true);
      setErr("");
      try {
         await api.post(`/projects/${projectId}/pid/create-index`, {});
         await loadIndex();
         await loadState();
      } catch (e) {
         setErr(formatErr(e?.response?.data?.detail) || e.message);
      } finally {
         setCreating(false);
      }
   };

   const generateOne = async (sheetNumber, sheetId) => {
      // Optimistic state
      setState((prev) => {
         const rows = (prev?.rows || []).map((r) =>
            r.sheet_id === sheetId ? { ...r, status: "generating" } : r,
         );
         return { ...(prev || {}), rows };
      });
      try {
         await api.post(`/projects/${projectId}/pid/generate/${sheetNumber}`);
         await loadState();
         await loadDrawings();
         openInViewer(sheetId);
      } catch (e) {
         setErr(formatErr(e?.response?.data?.detail) || e.message);
         setState((prev) => {
            const rows = (prev?.rows || []).map((r) =>
               r.sheet_id === sheetId ? { ...r, status: "failed" } : r,
            );
            return { ...(prev || {}), rows };
         });
      }
   };

   const generateAll = async () => {
      setGeneratingAll(true);
      setErr("");
      try {
         if (unitFilter === "all") {
            await api.post(`/projects/${projectId}/pid/generate-all`, {});
         } else {
            // Generate only sheets for the selected unit. Call per-sheet
            // sequentially so each row's status flips in real time.
            const targets = (index?.sheets || []).filter(
               (s) => (s.unit_key || s.unit_name) === unitFilter,
            );
            for (const s of targets) {
               try {
                  await api.post(`/projects/${projectId}/pid/generate/${s.sheet_number}`);
               } catch (_e) { /* per-sheet errors are surfaced via state poll */ }
               // Refresh state between renders so the UI shows progress
               if (targets.length < 20 || targets.indexOf(s) % 5 === 0) {
                  await loadState();
               }
            }
         }
         await loadState();
         await loadDrawings();
      } catch (e) {
         setErr(formatErr(e?.response?.data?.detail) || e.message);
      } finally {
         setGeneratingAll(false);
      }
   };

   const openInViewer = useCallback(async (sheetId) => {
      if (!drawings[sheetId] || !drawings[sheetId].canvas_svg) {
         try {
            const r = await api.get(`/projects/${projectId}/drawings/${sheetId}`);
            setDrawings((p) => ({ ...p, [sheetId]: r.data }));
         } catch { return; }
      }
      setOpenSheetIds((p) => p.includes(sheetId) ? p : [...p, sheetId]);
      setActiveSheetId(sheetId);
      setActiveTab("viewer");
   }, [projectId, drawings]);

   // Pan / zoom in viewer (CH.3 — pan from anywhere except labeled
   // streams/equipment; works as a single click otherwise.)
   const onCanvasMouseDown = (e) => {
      if (e.button !== 0) return;
      if (e.target.closest('[data-stream-id]')
          || e.target.closest('[data-equipment-tag]')
          || e.target.closest('[data-opc-target-sheet]')) return;
      e.preventDefault();
      isPanningRef.current = true;
      panStartRef.current = { x: e.clientX, y: e.clientY,
         panX: viewerPan.x, panY: viewerPan.y };
   };
   const onCanvasMouseMove = (e) => {
      if (!isPanningRef.current) return;
      setViewerPan({
         x: panStartRef.current.panX + (e.clientX - panStartRef.current.x),
         y: panStartRef.current.panY + (e.clientY - panStartRef.current.y),
      });
   };
   const onCanvasMouseUp = () => { isPanningRef.current = false; };
   const onCanvasWheel = (e) => {
      if (e.shiftKey) return;
      e.preventDefault();
      const step = (e.ctrlKey || e.metaKey) ? 0.05 : 0.15;
      setViewerZoom((z) => Math.max(0.2, Math.min(4,
         +(z + (e.deltaY > 0 ? -step : step)).toFixed(2))));
   };

   useEffect(() => {
      if (!viewerFullscreen) return undefined;
      const onKey = (e) => { if (e.key === "Escape") setViewerFullscreen(false); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
   }, [viewerFullscreen]);

   // CH.3 — Keyboard shortcuts (0, +, -, F, Esc).
   useEffect(() => {
      if (activeTab !== "viewer") return;
      const onKey = (e) => {
         if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA"
             || e.target?.isContentEditable) return;
         if (e.key === "0") { setViewerZoom(1); setViewerPan({ x: 0, y: 0 }); }
         else if (e.key === "+" || e.key === "=") setViewerZoom((z) => Math.min(4, +(z + 0.15).toFixed(2)));
         else if (e.key === "-" || e.key === "_") setViewerZoom((z) => Math.max(0.2, +(z - 0.15).toFixed(2)));
         else if (e.key === "f" || e.key === "F") setViewerFullscreen((v) => !v);
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
   }, [activeTab]);

   // ── Derived state ──
   const sheets = index?.sheets || [];
   const rowsByNumber = {};
   for (const r of (state?.rows || [])) {
      rowsByNumber[r.sheet_number] = r;
   }
   const totalSheets = index?.total_sheets || sheets.length;
   const completeCount = (state?.rows || []).filter(r => r.status === "complete").length;

   // Unit groupings (for filter dropdown)
   const unitCounts = {};
   for (const s of sheets) {
      const k = s.unit_key || s.unit_name || "other";
      if (!unitCounts[k]) {
         unitCounts[k] = { key: k, name: s.unit_name || k, count: 0 };
      }
      unitCounts[k].count += 1;
   }
   const unitsList = Object.values(unitCounts).sort(
      (a, b) => a.name.localeCompare(b.name),
   );

   // Filter sheets by selected unit
   const visibleSheets = unitFilter === "all"
      ? sheets
      : sheets.filter(
            (s) => (s.unit_key || s.unit_name) === unitFilter,
         );

   // ── Render ──
   if (!index || !sheets.length) {
      return (
         <div data-testid="pid-empty-state"
              style={{ padding: 32, color: COL.text, ...mono }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10,
                            marginBottom: 16 }}>
               <FileText size={20} color={COL.spark}/>
               <h2 style={{ margin: 0, fontSize: 18 }}>
                  B.3 — Piping & Instrumentation Diagrams
               </h2>
            </div>
            <p style={{ color: COL.dim, marginBottom: 24 }}>
               No P&ID register yet. Click below to build the
               industry-standard P&ID document index for this project
               (deterministic, no AI calls, instant).
            </p>
            {err && (
               <div style={{ background: "#7f1d1d", padding: 12, borderRadius: 4,
                                marginBottom: 16, color: "#fee2e2" }}>
                  <AlertCircle size={14} style={{ display: "inline", marginRight: 6 }}/>
                  {err}
               </div>
            )}
            <button data-testid="pid-create-index-btn"
                    onClick={createIndex} disabled={creating}
                    style={{ background: COL.spark, color: "#020617",
                              padding: "10px 18px", border: "none",
                              borderRadius: 4, cursor: "pointer",
                              fontWeight: 600, ...mono }}>
               {creating ? (
                  <><Loader2 size={14} style={{ display: "inline", marginRight: 8,
                                                    animation: "spin 1s linear infinite" }}/>
                     Building register...</>
               ) : (
                  "Build P&ID Register"
               )}
            </button>
         </div>
      );
   }

   return (
      <div data-testid="pid-document-register"
            style={{ color: COL.text, ...mono, height: "100%",
                      display: "flex", flexDirection: "column" }}>
         {/* Header */}
         <div style={{ borderBottom: `1px solid ${COL.border}`,
                          padding: "12px 20px", display: "flex",
                          alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700,
                            letterSpacing: 0.6 }}>
               B.3 — PIPING &amp; INSTRUMENTATION DIAGRAMS
            </h2>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
               <button data-testid="pid-tab-documents"
                       onClick={() => setActiveTab("documents")}
                       style={tabBtn(activeTab === "documents")}>
                  <FileText size={12} style={{ display: "inline",
                                                  marginRight: 4 }}/>
                  Documents <span style={{ color: COL.dim,
                                              marginLeft: 6 }}>
                     {completeCount} / {totalSheets}
                  </span>
               </button>
               <button data-testid="pid-tab-viewer"
                       onClick={() => setActiveTab("viewer")}
                       style={tabBtn(activeTab === "viewer")}>
                  <Eye size={12} style={{ display: "inline",
                                            marginRight: 4 }}/>
                  P&amp;ID Viewer
               </button>
               <button data-testid="pid-tab-diff"
                       onClick={() => setActiveTab("diff")}
                       style={tabBtn(activeTab === "diff")}
                       title="Compare any two revisions of the active P&ID">
                  <GitCompareArrows size={12} style={{ display: "inline",
                                                  marginRight: 4 }}/>
                  Diff
               </button>
               <button data-testid="pid-tab-editor"
                       onClick={() => setActiveTab("editor")}
                       style={tabBtn(activeTab === "editor")}
                       title="Open interactive editor for the active P&ID sheet">
                  <Pencil size={12} style={{ display: "inline",
                                                  marginRight: 4 }}/>
                  Editor
               </button>
            </div>
         </div>

         {err && (
            <div style={{ background: "#7f1d1d", padding: 10,
                            color: "#fee2e2", fontSize: 12 }}>
               <AlertCircle size={12} style={{ display: "inline",
                                                  marginRight: 6 }}/>
               {err}
               <button onClick={() => setErr("")}
                       style={{ float: "right", background: "transparent",
                                  border: "none", color: "#fee2e2",
                                  cursor: "pointer" }}>×</button>
            </div>
         )}

         {/* Body */}
         {activeTab === "documents" && (
            <DocumentsView
               index={index}
               state={state}
               sheets={visibleSheets}
               allSheetCount={sheets.length}
               rowsByNumber={rowsByNumber}
               drawings={drawings}
               totalSheets={totalSheets}
               completeCount={completeCount}
               generatingAll={generatingAll}
               unitsList={unitsList}
               unitFilter={unitFilter}
               setUnitFilter={setUnitFilter}
               onGenerateOne={generateOne}
               onGenerateAll={generateAll}
               onReCreateIndex={createIndex}
               creating={creating}
               onOpenViewer={openInViewer}
               onOpenEditor={(sheetId) => {
                  setActiveSheetId(sheetId);
                  setActiveTab("editor");
               }}/>
         )}
         {activeTab === "viewer" && (
            <ViewerView
               drawings={drawings}
               openSheetIds={openSheetIds}
               activeSheetId={activeSheetId}
               setActiveSheetId={setActiveSheetId}
               sheets={sheets}
               viewerZoom={viewerZoom}
               setViewerZoom={setViewerZoom}
               viewerPan={viewerPan}
               setViewerPan={setViewerPan}
               viewerFullscreen={viewerFullscreen}
               setViewerFullscreen={setViewerFullscreen}
               onCanvasMouseDown={onCanvasMouseDown}
               onCanvasMouseMove={onCanvasMouseMove}
               onCanvasMouseUp={onCanvasMouseUp}
               onCanvasWheel={onCanvasWheel}
               isPanningRef={isPanningRef}
               projectId={projectId}
               setDrawings={setDrawings}
               onOpenEditor={() => setActiveTab("editor")}
               onOpenInteractive={(sheetNumber, svg) =>
                  setInteractiveSheet({ sheetNumber, svg })}/>
         )}
         {interactiveSheet && (
            <InteractivePidPanel
               projectId={projectId}
               sheetNumber={interactiveSheet.sheetNumber}
               initialSvg={interactiveSheet.svg}
               onClose={() => setInteractiveSheet(null)}/>
         )}
         {activeTab === "diff" && (
            <PidDiffPanel
               projectId={projectId}
               sheetId={activeSheetId
                   || drawings[Object.keys(drawings)[0]]?.sheet_id
                   || null}
               sheetTitle={
                  drawings[activeSheetId]?.title
                  || sheets.find((s) => s.sheet_id === activeSheetId)?.title
               }/>
         )}
         {activeTab === "editor" && (
            <div style={{ flex: 1, minHeight: 0, display: "flex",
                                flexDirection: "column" }}
                  data-testid="pid-editor-tab">
               {(() => {
                  const sid = activeSheetId
                     || (openSheetIds && openSheetIds[0])
                     || (sheets && sheets[0]?.sheet_id)
                     || (drawings && Object.keys(drawings)[0])
                     || null;
                  if (!sid) {
                     return (
                        <div style={{ padding: 24, color: COL.dim,
                                          fontSize: 12 }}>
                           No P&amp;ID sheets exist yet — generate at
                           least one P&amp;ID first, then return here to
                           edit interactively.
                        </div>
                     );
                  }
                  return (
                     <PidEditor key={sid} projectId={projectId} sheetId={sid}
                        sheetTitle={
                           drawings[sid]?.title
                           || sheets.find((s) => s.sheet_id === sid)?.title
                        }
                        onRequestClose={() => setActiveTab("viewer")}
                        onSaved={() => { loadDrawings?.(); }}/>
                  );
               })()}
            </div>
         )}
      </div>
   );
}

function tabBtn(active) {
   return {
      background: active ? "var(--pf-spark, #22d3ee)" : "transparent",
      color: active ? "#020617" : "var(--pf-text-muted, #94a3b8)",
      border: `1px solid ${active ? "var(--pf-spark)" : "var(--pf-border)"}`,
      padding: "6px 12px", borderRadius: 4, cursor: "pointer",
      fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace",
   };
}

// ── Documents-view component ──
function DocumentsView({ index, state: _state, sheets, allSheetCount,
                            rowsByNumber, drawings, totalSheets,
                            completeCount, generatingAll, unitsList,
                            unitFilter, setUnitFilter,
                            onGenerateOne, onGenerateAll,
                            onReCreateIndex, creating, onOpenViewer,
                            onOpenEditor }) {
   const filtered = unitFilter !== "all";
   return (
      <div style={{ flex: 1, overflow: "auto" }}>
         {/* Toolbar */}
         <div style={{ padding: "16px 20px",
                          borderBottom: "1px solid var(--pf-border)",
                          display: "flex", alignItems: "center", gap: 16,
                          flexWrap: "wrap" }}>
            <div>
               <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {index.project || "Project"} — {totalSheets} P&amp;ID document{totalSheets !== 1 ? "s" : ""}
                  {filtered && (
                     <span style={{ color: COL.spark, marginLeft: 10,
                                       fontSize: 13 }}>
                        · showing {sheets.length} for {unitsList.find(u => u.key === unitFilter)?.name || unitFilter}
                     </span>
                  )}
               </div>
               <div style={{ fontSize: 11, color: COL.dim, marginTop: 4 }}>
                  Plant type:&nbsp;<span style={{ color: COL.spark }}>
                     {index.sub_industry_name || index.plant_type || "—"}
                  </span>
                  &nbsp;·&nbsp;
                  Complexity: {index.complexity_label || "Medium"}
                  &nbsp;·&nbsp;
                  Complete: <span style={{ color: COL.green }}>{completeCount}</span> / {allSheetCount}
               </div>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8,
                            alignItems: "center", flexWrap: "wrap" }}>
               {unitsList.length > 1 && (
                  <select data-testid="pid-unit-filter"
                          value={unitFilter}
                          onChange={(e) => setUnitFilter(e.target.value)}
                          style={selectStyle}>
                     <option value="all">All units · {allSheetCount} sheets</option>
                     {unitsList.map((u) => (
                        <option key={u.key} value={u.key}>
                           {u.name} · {u.count} sheet{u.count !== 1 ? "s" : ""}
                        </option>
                     ))}
                  </select>
               )}
               <button data-testid="pid-generate-all-btn"
                       onClick={onGenerateAll} disabled={generatingAll}
                       style={btnPrimary}>
                  {generatingAll ? (
                     <><Loader2 size={12} style={{ display: "inline",
                                                       marginRight: 6,
                                                       animation: "spin 1s linear infinite" }}/>
                        Generating...</>
                  ) : (
                     <><Play size={12} style={{ display: "inline",
                                                  marginRight: 6 }}/>
                        {filtered
                           ? `Generate ${sheets.length} ${unitsList.find(u => u.key === unitFilter)?.name?.split(' ')[0] || ''}`
                           : "Generate All"}</>
                  )}
               </button>
               <button data-testid="pid-recreate-index-btn"
                       onClick={onReCreateIndex} disabled={creating}
                       style={btnSecondary}>
                  <RefreshCcw size={12} style={{ display: "inline",
                                                     marginRight: 6 }}/>
                  Re-create Index
               </button>
            </div>
         </div>

         {/* Table */}
         <table style={{ width: "100%", borderCollapse: "collapse",
                            fontSize: 12 }}>
            <thead>
               <tr style={{ background: COL.surface }}>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>DOCUMENT NO.</th>
                  <th style={thStyle}>TITLE</th>
                  <th style={thStyle}>UNIT</th>
                  <th style={thStyle}>STATUS</th>
                  <th style={thStyle}>ACTION</th>
               </tr>
            </thead>
            <tbody>
               {sheets.map((sheet) => {
                  const row = rowsByNumber[sheet.sheet_number] || {};
                  const status = row.status || "not_started";
                  const sheetId = sheet.type === "overview"
                     ? "PID-OVERVIEW"
                     : `PID-${String(sheet.sheet_number).padStart(3, "0")}`;
                  const hasDrawing = drawings[sheetId]?.canvas_svg
                                       || drawings[sheetId]?.svg;
                  return (
                     <tr key={sheetId}
                         data-testid={`pid-row-${sheetId}`}
                         style={{ borderBottom: "1px solid var(--pf-border)" }}>
                        <td style={tdStyle}>{sheet.sheet_number}</td>
                        <td style={{ ...tdStyle, color: COL.spark,
                                       fontWeight: 600 }}>{sheetId}</td>
                        <td style={tdStyle}>
                           <div>{sheet.title}</div>
                           {sheet.description && (
                              <div style={{ color: COL.dim, fontSize: 10,
                                              marginTop: 4 }}>
                                 {sheet.description}
                              </div>
                           )}
                        </td>
                        <td style={{ ...tdStyle, color: COL.dim }}>
                           {sheet.unit_name || sheet.unit_key || "—"}
                        </td>
                        <td style={tdStyle}>
                           {STATUS_ICONS[status]} {status}
                        </td>
                        <td style={tdStyle}>
                           {(status === "complete" || hasDrawing) ? (
                              <div style={{ display: "flex", gap: 4,
                                                  flexWrap: "wrap" }}>
                                 <button data-testid={`pid-view-${sheetId}`}
                                         onClick={() => onOpenViewer(sheetId)}
                                         style={btnSmall}>
                                    <Eye size={11} style={{ display: "inline",
                                                               marginRight: 4 }}/>
                                    View
                                 </button>
                                 {onOpenEditor && (
                                    <button data-testid={`pid-launch-editor-${sheetId}`}
                                            onClick={() => onOpenEditor(sheetId)}
                                            title="Open this P&ID in the interactive editor"
                                            style={{ ...btnSmall,
                                               background: "rgba(34,211,238,0.15)",
                                               color: COL.spark,
                                               border: `1px solid ${COL.spark}` }}>
                                       <Pencil size={11} style={{ display: "inline",
                                                                     marginRight: 4 }}/>
                                       Edit
                                    </button>
                                 )}
                              </div>
                           ) : (
                              <button data-testid={`pid-generate-${sheetId}`}
                                      onClick={() => onGenerateOne(sheet.sheet_number, sheetId)}
                                      disabled={status === "generating"}
                                      style={btnSmall}>
                                 {status === "generating" ? (
                                    <><Loader2 size={11} style={{ display: "inline",
                                                                      marginRight: 4,
                                                                      animation: "spin 1s linear infinite" }}/>
                                       Generating...</>
                                 ) : (
                                    <><Play size={11} style={{ display: "inline",
                                                                  marginRight: 4 }}/>
                                       Generate</>
                                 )}
                              </button>
                           )}
                        </td>
                     </tr>
                  );
               })}
            </tbody>
         </table>
      </div>
   );
}

// ── Viewer component ──
function ViewerView({ drawings, openSheetIds, activeSheetId, setActiveSheetId,
                       sheets, viewerZoom, setViewerZoom, viewerPan,
                       setViewerPan, viewerFullscreen, setViewerFullscreen,
                       onCanvasMouseDown, onCanvasMouseMove,
                       onCanvasMouseUp, onCanvasWheel, isPanningRef,
                       projectId, setDrawings,
                       onOpenEditor, onOpenInteractive }) {
   const active = activeSheetId ? drawings[activeSheetId] : null;
   const svg = active?.canvas_svg || active?.svg || "";
   const containerStyle = viewerFullscreen ? {
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 100, background: COL.bg,
   } : { flex: 1, display: "flex", flexDirection: "column" };
   return (
      <div style={containerStyle}>
         {/* Tabs */}
         {openSheetIds.length > 0 && (
            <div style={{ display: "flex", gap: 4, padding: "8px 12px",
                            background: COL.surface,
                            borderBottom: "1px solid var(--pf-border)",
                            overflowX: "auto" }}>
               {openSheetIds.map((sid) => {
                  const sheet = sheets.find((s) => {
                     const sheetId = s.type === "overview"
                        ? "PID-OVERVIEW"
                        : `PID-${String(s.sheet_number).padStart(3, "0")}`;
                     return sheetId === sid;
                  });
                  return (
                     <button key={sid}
                             data-testid={`pid-tab-${sid}`}
                             onClick={() => setActiveSheetId(sid)}
                             style={{
                                background: sid === activeSheetId ? COL.spark : "transparent",
                                color: sid === activeSheetId ? "#020617" : COL.text,
                                border: `1px solid ${sid === activeSheetId ? COL.spark : COL.border}`,
                                padding: "4px 10px", borderRadius: 3,
                                cursor: "pointer", fontSize: 11, ...mono,
                                whiteSpace: "nowrap",
                             }}>
                        {sid} {sheet?.title ? `· ${sheet.title.slice(0, 40)}` : ""}
                     </button>
                  );
               })}
            </div>
         )}
         {/* Toolbar — just the Edit shortcut now; APS Viewer handles
             pan / zoom / fit / fullscreen natively. */}
         <div style={{ display: "flex", gap: 8, padding: "8px 12px",
                          background: COL.surface,
                          borderBottom: "1px solid var(--pf-border)",
                          alignItems: "center" }}>
            <span style={{ color: COL.dim, fontSize: 10 }}>
               Powered by Autodesk Forge Viewer (APS SVF2)
            </span>
            <div style={{ flex: 1 }}/>
            {onOpenEditor && activeSheetId && (
               <>
                  <button data-testid="pid-viewer-open-interactive"
                          onClick={() => {
                             // Extract sheet_number from active sheet.
                             const sheet = sheets.find((s) => {
                                const sid = s.type === "overview"
                                   ? "PID-OVERVIEW"
                                   : `PID-${String(s.sheet_number).padStart(3, "0")}`;
                                return sid === activeSheetId;
                             });
                             if (sheet && sheet.sheet_number != null
                                    && onOpenInteractive) {
                                onOpenInteractive(sheet.sheet_number,
                                                       drawings[activeSheetId]?.canvas_svg || "");
                             }
                          }}
                          title="Open interactive P&ID (click-to-edit, tag search, export BOM/DXF)"
                          style={{ ...btnIcon,
                             background: "rgba(250,204,21,0.15)",
                             color: "#facc15",
                             border: `1px solid #facc15`,
                             padding: "4px 10px", marginRight: 8,
                             display: "flex", alignItems: "center", gap: 4 }}>
                     <MousePointerClick size={12}/>
                     <span style={{ fontSize: 11, ...mono }}>Interactive</span>
                  </button>
                  <button data-testid="pid-viewer-open-editor"
                          onClick={onOpenEditor}
                          title="Open this P&ID in the interactive editor"
                          style={{ ...btnIcon,
                             background: "rgba(34,211,238,0.15)",
                             color: COL.spark,
                             border: `1px solid ${COL.spark}`,
                             padding: "4px 10px",
                             display: "flex", alignItems: "center", gap: 4 }}>
                     <Pencil size={12}/>
                     <span style={{ fontSize: 11, ...mono }}>Edit</span>
                  </button>
               </>
            )}
         </div>
         {/* APS Viewer canvas (replaces legacy SVG render).
             The Autodesk Forge / APS Viewer handles pan, zoom,
             measure, markup, and fullscreen natively — so the
             previous dot-grid background + floating pill toolbar
             are no longer needed in this pane. */}
         <div style={{ flex: 1, minHeight: 0, position: "relative",
                          display: "flex", flexDirection: "column",
                          background: "#020617" }}>
            {activeSheetId ? (
               <ApsDrawingViewer
                  key={activeSheetId}
                  projectId={projectId}
                  sheetId={activeSheetId}
                  docKind="pid"
                  title={active?.title
                     || sheets.find((s) => {
                        const sid = s.type === "overview"
                           ? "PID-OVERVIEW"
                           : `PID-${String(s.sheet_number).padStart(3, "0")}`;
                        return sid === activeSheetId;
                     })?.title
                     || activeSheetId}
                  svgHash={_hashSvgFnv(svg)}
                  onExit={onOpenEditor}/>
            ) : (
               <div data-testid="pid-viewer-empty-state"
                  style={{ flex: 1, display: "flex",
                     flexDirection: "column", alignItems: "center",
                     justifyContent: "center", gap: 12,
                     color: "#94a3b8", textAlign: "center",
                     fontSize: 13 }}>
                  Click a P&amp;ID in the&nbsp;
                  <span style={{ color: "#22d3ee" }}>Documents</span>
                  &nbsp;tab to open it here.
               </div>
            )}
         </div>
      </div>
   );
}

// CH.3 — Figma/Miro-style floating pill button (shared style).
function canvasPillBtn(disabled, active) {
   return {
      background: active ? "rgba(34,211,238,0.18)" : "transparent",
      border: 0,
      color: active ? "var(--pf-spark, #22d3ee)" : "#e2e8f0",
      padding: "6px 10px", borderRadius: 999,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.4 : 1,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      transition: "background 120ms ease, color 120ms ease",
   };
}

const selectStyle = {
   background: "var(--pf-surface, #0f1729)",
   color: "var(--pf-text, #e2e8f0)",
   border: "1px solid var(--pf-spark, #22d3ee)",
   padding: "6px 10px", borderRadius: 3, cursor: "pointer",
   fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
   outline: "none",
};
const thStyle = {
   textAlign: "left", padding: "10px 16px", color: "var(--pf-text-muted, #94a3b8)",
   fontWeight: 600, fontSize: 11, letterSpacing: 0.5,
   borderBottom: "1px solid var(--pf-border)",
};
const tdStyle = { padding: "12px 16px", verticalAlign: "top", fontSize: 12 };
const btnPrimary = {
   background: "var(--pf-spark, #22d3ee)", color: "#020617",
   border: "none", padding: "6px 12px", borderRadius: 3, cursor: "pointer",
   fontWeight: 600, fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
};
const btnSecondary = {
   background: "transparent", color: "var(--pf-text, #e2e8f0)",
   border: "1px solid var(--pf-border, #1e293b)", padding: "6px 12px",
   borderRadius: 3, cursor: "pointer", fontSize: 12,
   fontFamily: "'JetBrains Mono', monospace",
};
const btnSmall = {
   background: "transparent", color: "var(--pf-spark, #22d3ee)",
   border: "1px solid var(--pf-spark, #22d3ee)", padding: "4px 10px",
   borderRadius: 3, cursor: "pointer", fontSize: 11,
   fontFamily: "'JetBrains Mono', monospace",
};
const btnIcon = {
   background: "transparent", color: "var(--pf-text, #e2e8f0)",
   border: "1px solid var(--pf-border, #1e293b)", padding: 6,
   borderRadius: 3, cursor: "pointer",
   display: "flex", alignItems: "center", justifyContent: "center",
};
